import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingCart, ClipboardCheck, Clock, Users as UsersIcon,
  TrendingUp, Truck, CheckCircle2, FileCheck, FileX, FileText
} from "lucide-react";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card className="p-4 md:p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
          <div className="font-display font-bold text-2xl md:text-3xl mt-2">{value}</div>
        </div>
        <div className="p-2 rounded-md bg-muted">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
    </Card>
  );
}

const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const isoDate = (d) => d.toISOString().slice(0, 10);

function defaultRange() {
  return { start: "", end: "", preset: "all" };
}

function presetToRange(preset) {
  const today = new Date();
  const t = isoDate(today);
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const y = isoDate(yest);
  const last7 = new Date(today); last7.setDate(last7.getDate() - 6);
  const last30 = new Date(today); last30.setDate(last30.getDate() - 29);
  if (preset === "today") return { start: t, end: t, preset };
  if (preset === "yesterday") return { start: y, end: y, preset };
  if (preset === "7d") return { start: isoDate(last7), end: t, preset };
  if (preset === "30d") return { start: isoDate(last30), end: t, preset };
  return { start: "", end: "", preset: "all" };
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(defaultRange());

  const params = useMemo(() => {
    const p = {};
    if (range.start) p.start_date = range.start;
    if (range.end) p.end_date = range.end;
    return p;
  }, [range.start, range.end]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
    } catch {
      setStats({});
    }
  }, [params]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const showFilter = user.role === "admin" || user.role === "subadmin" || user.role === "sales";

  if (!stats) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
          Welcome back, {user.name.split(" ")[0]}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Here's an overview of activity</p>
      </div>

      {showFilter && (
        <Card className="p-4" data-testid="dashboard-date-filter">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "All time" },
                { key: "today", label: "Today" },
                { key: "yesterday", label: "Yesterday" },
                { key: "7d", label: "Last 7 days" },
                { key: "30d", label: "Last 30 days" },
              ].map((p) => (
                <Button
                  key={p.key}
                  type="button"
                  size="sm"
                  variant={range.preset === p.key ? "default" : "outline"}
                  onClick={() => setRange(presetToRange(p.key))}
                  data-testid={`dashboard-preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex items-end gap-2 ml-auto">
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={range.start}
                  max={range.end || undefined}
                  onChange={(e) => setRange({ ...range, start: e.target.value, preset: "custom" })}
                  className="h-9 w-40"
                  data-testid="dashboard-date-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={range.end}
                  min={range.start || undefined}
                  onChange={(e) => setRange({ ...range, end: e.target.value, preset: "custom" })}
                  className="h-9 w-40"
                  data-testid="dashboard-date-to"
                />
              </div>
              {(range.start || range.end) && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setRange(defaultRange())} data-testid="dashboard-clear">
                  Clear
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {(user.role === "admin" || user.role === "subadmin") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <StatCard testid="stat-total-orders" label="Total Orders" value={stats.total_orders} icon={ShoppingCart} />
          <StatCard testid="stat-manual-orders" label="Manual Orders" value={stats.manual_orders} icon={ClipboardCheck} />
          <StatCard testid="stat-approved-claims" label="Approved Claim Orders" value={stats.approved_claim_orders} icon={CheckCircle2} />
          <StatCard testid="stat-pending-claims" label="Pending Claims" value={stats.pending_claim_orders} icon={Clock} />
          <StatCard testid="stat-delivered" label="Delivered Orders" value={stats.delivered_orders || 0} icon={Truck} />
          <StatCard testid="stat-total-agents" label={user.role === "subadmin" ? "Assigned BDMs" : "Total BDMs"} value={stats.total_agents} icon={UsersIcon} />
          <StatCard testid="stat-total-sales" label="Total Turnover" value={fmtMoney(stats.total_sales_value)} icon={TrendingUp} />
          <StatCard testid="stat-turnover-manual" label="Turnover · Manual Orders" value={fmtMoney(stats.turnover_manual)} icon={TrendingUp} />
          <StatCard testid="stat-turnover-claimed" label="Turnover · Approved Claim Orders" value={fmtMoney(stats.turnover_claimed)} icon={FileCheck} />
          {user.role === "admin" && (
            <>
              <StatCard testid="stat-b2b-bills" label="B2B Bills Generated" value={stats.b2b_bills_generated || 0} icon={FileText} />
              <StatCard testid="stat-b2b-turnover" label="B2B Total Turnover" value={fmtMoney(stats.b2b_total_turnover)} icon={TrendingUp} />
            </>
          )}
        </div>
      )}

      {user.role === "sales" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <StatCard testid="stat-my-orders" label="My Total Orders" value={stats.my_total_orders} icon={ShoppingCart} />
          <StatCard testid="stat-my-manual" label="My Manual Orders" value={stats.my_manual_orders} icon={ClipboardCheck} />
          <StatCard testid="stat-my-approved" label="My Approved Claims" value={stats.my_approved_claims} icon={CheckCircle2} />
          <StatCard testid="stat-my-pending" label="My Pending Claims" value={stats.my_pending_claims} icon={Clock} />
          <StatCard testid="stat-my-delivered" label="Delivered Orders" value={stats.my_delivered_orders || 0} icon={Truck} />
          <StatCard testid="stat-my-sales" label="My Total Turnover" value={fmtMoney(stats.my_total_sales_value)} icon={TrendingUp} />
          <StatCard testid="stat-my-turnover-manual" label="Turnover · Manual Orders" value={fmtMoney(stats.my_turnover_manual)} icon={TrendingUp} />
          <StatCard testid="stat-my-turnover-claimed" label="Turnover · Approved Claim Orders" value={fmtMoney(stats.my_turnover_claimed)} icon={FileCheck} />
        </div>
      )}

      {user.role === "operations" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard testid="stat-ops-pending" label="Pending Fulfillment" value={stats.pending_fulfillment} icon={Clock} />
          <StatCard testid="stat-ops-fulfilled" label="Fulfilled" value={stats.fulfilled} icon={ClipboardCheck} />
          <StatCard testid="stat-ops-transit" label="In Transit" value={stats.in_transit} icon={Truck} />
          <StatCard testid="stat-ops-delivered" label="Delivered" value={stats.delivered} icon={CheckCircle2} />
        </div>
      )}

      {user.role === "approval" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard testid="stat-claims-total" label="Total Claims" value={stats.total_claims} icon={ClipboardCheck} />
          <StatCard testid="stat-claims-pending" label="Pending" value={stats.pending_claims} icon={Clock} />
          <StatCard testid="stat-claims-approved" label="Approved" value={stats.approved_claims} icon={FileCheck} />
          <StatCard testid="stat-claims-rejected" label="Rejected" value={stats.rejected_claims} icon={FileX} />
        </div>
      )}
    </div>
  );
}
